import { FeatureClientPlatform } from './context';
import {
  FeatureCategory,
  FeatureFlag,
  FeatureKey,
  FeatureRegistry,
  FeatureStage,
  FeatureVisibility,
} from './types';

export const FEATURE_REGISTRY: FeatureRegistry = {
  [FeatureKey.SOCIAL_FEED]: {
    key: FeatureKey.SOCIAL_FEED,
    name: 'Social Feed',
    description: 'Chronological creator content feed for consumers.',
    category: FeatureCategory.SOCIAL,
    defaultState: true,
    stage: FeatureStage.GENERAL_AVAILABILITY,
    visibility: FeatureVisibility.PUBLIC,
    audienceScopable: true,
  },
  [FeatureKey.ARTICLES]: {
    key: FeatureKey.ARTICLES,
    name: 'Articles',
    description:
      'Controls who can start new articles and edit published articles on the social app. Reading, search, feed cards, and engagement stay available when off; in-flight drafts can still be finished.',
    category: FeatureCategory.SOCIAL,
    defaultState: true,
    stage: FeatureStage.BETA,
    visibility: FeatureVisibility.CREATORS,
    audienceScopable: true,
  },
  [FeatureKey.DISCOVERY]: {
    key: FeatureKey.DISCOVERY,
    name: 'Discovery',
    description: 'Creator discovery surfaces and search.',
    category: FeatureCategory.SOCIAL,
    defaultState: true,
    stage: FeatureStage.GENERAL_AVAILABILITY,
    visibility: FeatureVisibility.PUBLIC,
    audienceScopable: true,
  },
  [FeatureKey.CREATOR_PROFILE]: {
    key: FeatureKey.CREATOR_PROFILE,
    name: 'Creator Profile',
    description: 'Public creator profile pages.',
    category: FeatureCategory.CREATOR_OS,
    defaultState: true,
    stage: FeatureStage.GENERAL_AVAILABILITY,
    visibility: FeatureVisibility.PUBLIC,
  },
  [FeatureKey.CREATOR_SITE]: {
    key: FeatureKey.CREATOR_SITE,
    name: 'Creator Site',
    description:
      'Personal brand website at loamify.io/{username} with Studio section builder. Unpublished creators keep the compact bio fallback.',
    category: FeatureCategory.CREATOR_OS,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.CREATORS,
    creatorOverrideable: true,
  },
  [FeatureKey.FOLLOW_SYSTEM]: {
    key: FeatureKey.FOLLOW_SYSTEM,
    name: 'Follow System',
    description: 'Consumer-to-creator follow graph.',
    category: FeatureCategory.SOCIAL,
    defaultState: true,
    stage: FeatureStage.GENERAL_AVAILABILITY,
    visibility: FeatureVisibility.PUBLIC,
    audienceScopable: true,
  },
  [FeatureKey.DIRECT_MESSAGES]: {
    key: FeatureKey.DIRECT_MESSAGES,
    name: 'Direct Messages',
    description: 'Member-to-member peer direct messaging.',
    category: FeatureCategory.SOCIAL,
    defaultState: true,
    stage: FeatureStage.GENERAL_AVAILABILITY,
    visibility: FeatureVisibility.PUBLIC,
    audienceScopable: true,
  },
  [FeatureKey.CREATOR_STUDIO]: {
    key: FeatureKey.CREATOR_STUDIO,
    name: 'Loamify Studio',
    description: 'Creator OS dashboard and management tools.',
    category: FeatureCategory.CREATOR_OS,
    defaultState: true,
    stage: FeatureStage.GENERAL_AVAILABILITY,
    visibility: FeatureVisibility.CREATORS,
  },
  [FeatureKey.AUDIENCE]: {
    key: FeatureKey.AUDIENCE,
    name: 'Audience',
    description: 'Creator-owned contacts and audience insights.',
    category: FeatureCategory.CREATOR_OS,
    defaultState: true,
    stage: FeatureStage.BETA,
    visibility: FeatureVisibility.CREATORS,
    creatorOverrideable: true,
  },
  [FeatureKey.CIRCLES]: {
    key: FeatureKey.CIRCLES,
    name: 'Circles',
    description:
      'Creator-led communities in Studio and on the social app. When off, published circles stay discoverable and existing members keep access; new joins and commerce pause. Studio shows existing circles in read-only suspended mode.',
    category: FeatureCategory.COMMERCE,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
    moduleProfile: 'social-commerce',
    requirements: {
      internalOnly: true,
      betaPrograms: ['circles_alpha'],
    },
  },
  [FeatureKey.NEWSLETTERS]: {
    key: FeatureKey.NEWSLETTERS,
    name: 'Newsletters',
    description: 'Creator newsletter publishing.',
    category: FeatureCategory.CREATOR_OS,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
  },
  [FeatureKey.EMAIL_MARKETING]: {
    key: FeatureKey.EMAIL_MARKETING,
    name: 'Email Marketing',
    description: 'Audience email campaigns and automation.',
    category: FeatureCategory.CREATOR_OS,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
  },
  [FeatureKey.AFFILIATE_MANAGER]: {
    key: FeatureKey.AFFILIATE_MANAGER,
    name: 'Affiliate Manager',
    description: 'Creator affiliate and partner management.',
    category: FeatureCategory.COMMERCE,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
  },
  [FeatureKey.REFERRAL_SYSTEM]: {
    key: FeatureKey.REFERRAL_SYSTEM,
    name: 'Referrals',
    description:
      'Member referral programs and attribution across commerce offers. Circle is the first scoped workspace; future offer types reuse the same Growth engine.',
    category: FeatureCategory.COMMERCE,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
  },
  [FeatureKey.ANALYTICS]: {
    key: FeatureKey.ANALYTICS,
    name: 'Analytics',
    description: 'Creator and platform analytics.',
    category: FeatureCategory.ANALYTICS,
    defaultState: false,
    stage: FeatureStage.ALPHA,
    visibility: FeatureVisibility.ADMIN,
    creatorOverrideable: true,
    requirements: {
      minStage: FeatureStage.ALPHA,
      subscriptionTiers: ['pro', 'business'],
    },
  },
  [FeatureKey.AI_ASSISTANT]: {
    key: FeatureKey.AI_ASSISTANT,
    name: 'AI Assistant',
    description: 'AI assistance for creators and operators.',
    category: FeatureCategory.AI,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    studioBacklog: true,
    requirements: {
      minStage: FeatureStage.EXPERIMENTAL,
      betaPrograms: ['ai_assistant_preview'],
    },
  },
  [FeatureKey.AI_CONTENT]: {
    key: FeatureKey.AI_CONTENT,
    name: 'AI Content',
    description: 'AI-assisted content creation.',
    category: FeatureCategory.AI,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    studioBacklog: true,
    requirements: {
      minStage: FeatureStage.EXPERIMENTAL,
      betaPrograms: ['ai_content_preview'],
    },
  },
  [FeatureKey.AI_AUTOMATION]: {
    key: FeatureKey.AI_AUTOMATION,
    name: 'AI Automation',
    description: 'AI-powered workflow automation.',
    category: FeatureCategory.AI,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    studioBacklog: true,
    requirements: {
      minStage: FeatureStage.EXPERIMENTAL,
      betaPrograms: ['ai_automation_preview'],
    },
  },
  [FeatureKey.CUSTOM_DOMAIN]: {
    key: FeatureKey.CUSTOM_DOMAIN,
    name: 'Custom Domain',
    description: 'Custom domain support for creator pages.',
    category: FeatureCategory.PLATFORM,
    defaultState: false,
    stage: FeatureStage.ALPHA,
    visibility: FeatureVisibility.CREATORS,
    creatorOverrideable: true,
  },
  [FeatureKey.COURSES]: {
    key: FeatureKey.COURSES,
    name: 'Courses',
    description:
      'Course publishing and sales. When off, existing enrollments and obligations remain; new sales and publishing pause.',
    category: FeatureCategory.COMMERCE,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
    moduleProfile: 'social-commerce',
  },
  [FeatureKey.MEMBERSHIPS]: {
    key: FeatureKey.MEMBERSHIPS,
    name: 'Memberships',
    description:
      'Creator memberships with gated updates and external perks. When off, existing members keep access; new acquisition pauses.',
    category: FeatureCategory.COMMERCE,
    defaultState: true,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
    moduleProfile: 'social-commerce',
  },
  [FeatureKey.EVENTS]: {
    key: FeatureKey.EVENTS,
    name: 'Events',
    description:
      'Community events, calendar, and RSVP. When off, existing RSVPs and obligations remain; new paid registration pauses.',
    category: FeatureCategory.COMMERCE,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
    moduleProfile: 'social-commerce',
  },
  [FeatureKey.OFFERS]: {
    key: FeatureKey.OFFERS,
    name: 'Offers',
    description:
      'Canonical creator catalog: digital offers, 1:1 sessions, webinars, and services. Offer types can be toggled individually. When off, existing orders remain serviceable; new sales pause.',
    category: FeatureCategory.COMMERCE,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    creatorOverrideable: true,
    moduleProfile: 'social-commerce',
  },
  [FeatureKey.CREATOR_PAYOUTS]: {
    key: FeatureKey.CREATOR_PAYOUTS,
    name: 'Creator Payouts',
    description: 'Stripe Connect onboarding, payout dashboard, and creator ledger.',
    category: FeatureCategory.COMMERCE,
    defaultState: true,
    stage: FeatureStage.BETA,
    visibility: FeatureVisibility.CREATORS,
  },
  [FeatureKey.LIVE_STREAMING]: {
    key: FeatureKey.LIVE_STREAMING,
    name: 'Live Streaming',
    description: 'Live video and creator streaming.',
    category: FeatureCategory.SOCIAL,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    studioBacklog: true,
    audienceScopable: true,
  },
  [FeatureKey.MOBILE_APP]: {
    key: FeatureKey.MOBILE_APP,
    name: 'Mobile App',
    description: 'Mobile consumer and creator applications.',
    category: FeatureCategory.MOBILE,
    defaultState: false,
    stage: FeatureStage.ALPHA,
    visibility: FeatureVisibility.INTERNAL,
    requirements: {
      platforms: [
        FeatureClientPlatform.MOBILE_CONSUMER,
        FeatureClientPlatform.MOBILE_CREATOR,
      ],
    },
  },
  [FeatureKey.PUBLIC_API]: {
    key: FeatureKey.PUBLIC_API,
    name: 'Public API',
    description: 'External API access for integrations.',
    category: FeatureCategory.INTEGRATIONS,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    studioBacklog: true,
  },
  [FeatureKey.WEBHOOKS]: {
    key: FeatureKey.WEBHOOKS,
    name: 'Webhooks',
    description: 'Outbound webhook integrations.',
    category: FeatureCategory.INTEGRATIONS,
    defaultState: false,
    stage: FeatureStage.EXPERIMENTAL,
    visibility: FeatureVisibility.INTERNAL,
    studioBacklog: true,
  },
};

export const FEATURES = Object.freeze({ ...FeatureKey });

export function getDefaultFeatureRegistry(): FeatureRegistry {
  return FEATURE_REGISTRY;
}

export function getRegisteredFeatureKeys(): FeatureKey[] {
  return Object.values(FeatureKey);
}

export function listCreatorOverrideableFeatures(): FeatureFlag[] {
  return Object.values(FEATURE_REGISTRY).filter(
    (feature) => feature.creatorOverrideable === true && feature.studioBacklog !== true,
  );
}

export function isCreatorOverrideableFeature(key: FeatureKey): boolean {
  return FEATURE_REGISTRY[key].creatorOverrideable === true;
}

export function assertFeatureRegistered(key: FeatureKey): FeatureFlag {
  const feature = FEATURE_REGISTRY[key];
  if (!feature) {
    throw new Error(`Feature flag is not registered: ${key}`);
  }
  return feature;
}
